/**
 * Agenda de l'équipe (espace compétition)
 *
 * Prochains défis/entraînements + historique récent, proposer un défi,
 * caler un entraînement, accepter/refuser/annuler, gérer la feuille de match.
 */

import { useState, useEffect, useCallback } from 'react';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import { useProfile } from '../../contexts/ProfileContext';
import TeamPicker from '../../components/competitions/TeamPicker';
import MemberSelect from '../../components/competitions/MemberSelect';
import QuarterHourDateTimeInput from '../../components/competitions/QuarterHourDateTimeInput';
import {
  getMatches, createMatch, matchAction, setLineup, getLineup,
  type MyTeam, type MatchItem,
} from '../../lib/teamsApi';
import { CrossedSwordsTableIcon, TrainingIcon, CheckCircleIcon, CloseIcon, SaveIcon, ListGridIcon, WarningIcon } from '../../components/Icons';
import { BACKEND_URL } from '../../config';
import type { Session } from '../../types/app';

function fmtDateTime(v: string | null): string {
  if (!v) return '-';
  try {
    const d = new Date(v);
    return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return v;
  }
}

function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Arrondit une date au quart d'heure le plus proche (pour une valeur par défaut cohérente avec QuarterHourDateTimeInput) */
function roundToNextQuarterHour(d: Date): Date {
  const rounded = new Date(d);
  const remainder = rounded.getMinutes() % 15;
  if (remainder !== 0) rounded.setMinutes(rounded.getMinutes() + (15 - remainder));
  rounded.setSeconds(0, 0);
  return rounded;
}

function matchKindLabel(m: MatchItem): string {
  if (m.equipe2 === 2) return 'Entraînement';
  if (m.idcompetition === 1) return m.division ? `Championnat · ${m.division.nom}` : 'Championnat';
  if (m.idcompetition) return 'Coupe';
  return 'Libre';
}

/** Arrondit un score à 2 décimales sans zéros superflus (ex: 82 reste "82", 22.353 devient "22.35") */
function fmtScore(score: number): string {
  return String(Math.round(score * 100) / 100);
}

const WIN_COLOR = 'var(--success, #2e9e5b)';
const LOSS_COLOR = 'var(--danger, #d9534f)';

/** Rendu visuel (libellé + couleur + icône) des statuts non "actifs" d'un match, pour l'historique */
const STATE_META: Record<string, { label: string; color: string; icon: 'check' | 'close' | 'warning' }> = {
  fini: { label: 'Terminé', color: 'var(--text-secondary)', icon: 'check' },
  refus: { label: 'Refusé', color: LOSS_COLOR, icon: 'close' },
  abandon: { label: 'Abandonné', color: '#e0a030', icon: 'warning' },
  annule: { label: 'Annulé', color: 'var(--text-muted, var(--text-secondary))', icon: 'close' },
};

function StateBadge({ etat }: { etat: string }) {
  const meta = STATE_META[etat] || { label: etat, color: 'var(--text-secondary)', icon: 'check' as const };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: meta.color, fontWeight: 600 }}>
      {meta.icon === 'check' && <CheckCircleIcon size={13} />}
      {meta.icon === 'close' && <CloseIcon size={13} />}
      {meta.icon === 'warning' && <WarningIcon size={13} />}
      {meta.label}
    </span>
  );
}

const SLOT_LABELS: Record<string, string> = {
  attaquant: 'Attaquant', def_E: 'Défenseur Est', def_N: 'Défenseur Nord', def_O: 'Défenseur Ouest',
};

interface TeamAgendaViewProps {
  session: Session;
  team: MyTeam;
}

export default function TeamAgendaView({ session, team }: TeamAgendaViewProps) {
  const token = session.token!;
  const { openTeamProfile } = useProfile();
  const [matches, setMatches] = useState<MatchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [showChallengeForm, setShowChallengeForm] = useState(false);
  const [opponent, setOpponent] = useState<{ id: number; nom: string } | null>(null);
  const [rdv, setRdv] = useState(() => toDatetimeLocalValue(roundToNextQuarterHour(new Date(Date.now() + 24 * 3600 * 1000))));
  const [nbparties, setNbparties] = useState(4);
  const [surcontrats, setSurcontrats] = useState(false);
  const [attTournant, setAttTournant] = useState(false);

  const [showTrainingForm, setShowTrainingForm] = useState(false);
  const [trainingRdv, setTrainingRdv] = useState(() => toDatetimeLocalValue(roundToNextQuarterHour(new Date(Date.now() + 3600 * 1000))));
  const [trainingNbparties, setTrainingNbparties] = useState(4);

  const [lineupMatchId, setLineupMatchId] = useState<number | null>(null);
  const [lineupDraft, setLineupDraft] = useState({ attaquant: '', def_E: '', def_N: '', def_O: '', capitaine_defi: '' });

  // Un rendez-vous doit être pris avec au moins 5 minutes d'avance (évite les
  // défis/entraînements « dans le passé » ou dans la minute qui vient).
  const minRdv = toDatetimeLocalValue(new Date(Date.now() + 5 * 60 * 1000));
  const rdvTooSoon = new Date(rdv).getTime() < Date.now() + 5 * 60 * 1000;
  const trainingRdvTooSoon = new Date(trainingRdv).getTime() < Date.now() + 5 * 60 * 1000;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getMatches({ team_id: team.id, limit: 100, token })
      .then((d) => setMatches(d.items))
      .catch(() => setError('Impossible de charger l\'agenda'))
      .finally(() => setLoading(false));
  }, [team.id, token]);

  useEffect(() => { load(); }, [load]);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      load();
    } catch {
      setError('Action impossible');
    } finally {
      setBusy(false);
    }
  };

  const now = Date.now();
  const isActiveState = (m: MatchItem) => ['proposition', 'ok', 'encours'].includes(m.etat);
  const upcoming = matches
    .filter((m) => isActiveState(m) && new Date(m.rdv).getTime() >= now)
    .sort((a, b) => new Date(a.rdv).getTime() - new Date(b.rdv).getTime());
  // Propositions/matchs jamais résolus (accepté/refusé/annulé) dont la date est dépassée :
  // on les distingue de "À venir" (qui doit rester une vraie liste chronologique utile)
  // sans pour autant les faire disparaître, car le staff peut encore devoir les traiter.
  const staleActive = matches
    .filter((m) => isActiveState(m) && new Date(m.rdv).getTime() < now)
    .sort((a, b) => new Date(b.rdv).getTime() - new Date(a.rdv).getTime());
  const past = matches
    .filter((m) => !isActiveState(m))
    .sort((a, b) => new Date(b.rdv || b.quand).getTime() - new Date(a.rdv || a.quand).getTime());

  const openLineupEditor = (m: MatchItem) => {
    setLineupMatchId(m.id);
    getLineup(token, m.id)
      .then((res) => {
        const idx = m.equipe1 === team.id ? 1 : 2;
        const data = res as { lineups?: Record<string, { attaquant?: string; def_E?: string; def_N?: string; def_O?: string; capitaine_defi?: string } | null> };
        const existing = data.lineups?.[String(idx)];
        setLineupDraft({
          attaquant: existing?.attaquant || '',
          def_E: existing?.def_E || '',
          def_N: existing?.def_N || '',
          def_O: existing?.def_O || '',
          capitaine_defi: existing?.capitaine_defi || '',
        });
      })
      .catch(() => setLineupDraft({ attaquant: '', def_E: '', def_N: '', def_O: '', capitaine_defi: '' }));
  };

  const rosterOptions = team.members;

  // Un joueur ne peut occuper qu'une seule position de la feuille de match :
  // en choisir un déjà affecté ailleurs le retire de son ancienne position.
  const assignLineupSlot = (slot: 'attaquant' | 'def_E' | 'def_N' | 'def_O', pseudo: string) => {
    setLineupDraft((prev) => {
      const next = { ...prev, [slot]: pseudo };
      if (pseudo) {
        (['attaquant', 'def_E', 'def_N', 'def_O'] as const).forEach((s) => {
          if (s !== slot && next[s] === pseudo) next[s] = '';
        });
      }
      if (next.capitaine_defi && ![next.attaquant, next.def_E, next.def_N, next.def_O].includes(next.capitaine_defi)) {
        next.capitaine_defi = '';
      }
      return next;
    });
  };

  const renderActiveMatch = (m: MatchItem, opts?: { stale?: boolean }) => {
    const isOpponent = m.equipe2 === team.id;
    const isProposer = m.equipe1 === team.id;
    const opponentName = m.equipe2 === 2 ? 'Entraînement' : (isProposer ? m.equipe2_nom : m.equipe1_nom) || `Équipe #${isProposer ? m.equipe2 : m.equipe1}`;
    const isTraining = m.equipe2 === 2;
    return (
      <div key={m.id} className="competition-card" style={{ cursor: 'default', marginBottom: 8, alignItems: 'flex-start' }}>
        {isTraining ? (
          <span className="tm-training-icon"><TrainingIcon size={22} /></span>
        ) : (
          <PlayerAvatar
            pseudo={opponentName}
            size={36}
            photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(opponentName)}?size=72`}
          />
        )}
        <div className="competition-card-text" style={{ flex: 1 }}>
          <button
            className="competition-card-title"
            style={{ background: 'none', border: 'none', padding: 0, cursor: isTraining ? 'default' : 'pointer', textAlign: 'left' }}
            onClick={() => { if (!isTraining) openTeamProfile(isProposer ? m.equipe2 : m.equipe1); }}
          >
            {isTraining ? opponentName : `vs ${opponentName}`}
          </button>
          <span className="competition-card-subtitle">
            {fmtDateTime(m.rdv)} · {m.nbparties} partie{m.nbparties > 1 ? 's' : ''} · {matchKindLabel(m)}
            {' · '}
            {opts?.stale
              ? <span style={{ color: 'var(--danger, #d9534f)' }}>en attente depuis longtemps, à traiter</span>
              : m.etat}
          </span>
          {lineupMatchId === m.id && (
            <div className="tm-form-card" style={{ marginTop: 10 }}>
              <div className="tm-form-row">
                {(['attaquant', 'def_E', 'def_N', 'def_O'] as const).map((slot) => (
                  <div key={slot} className="tm-field">
                    <span>{SLOT_LABELS[slot]}</span>
                    <MemberSelect
                      value={lineupDraft[slot]}
                      onChange={(pseudo) => assignLineupSlot(slot, pseudo)}
                      options={rosterOptions}
                      emptyLabel="—"
                    />
                  </div>
                ))}
              </div>
              <div className="tm-field" style={{ maxWidth: 220 }}>
                <span>Capitaine de défi</span>
                <MemberSelect
                  value={lineupDraft.capitaine_defi}
                  onChange={(pseudo) => setLineupDraft((prev) => ({ ...prev, capitaine_defi: pseudo }))}
                  options={[lineupDraft.attaquant, lineupDraft.def_E, lineupDraft.def_N, lineupDraft.def_O]
                    .filter(Boolean)
                    .map((p) => ({ pseudo: p }))}
                  emptyLabel="—"
                />
              </div>
              <div className="tm-toolbar">
                <button className="cia-btn cia-btn--ghost cia-btn--with-icon" disabled={busy} onClick={() => setLineupMatchId(null)}>
                  <CloseIcon size={14} /> Fermer
                </button>
                <button
                  className="cia-btn cia-btn--primary cia-btn--with-icon"
                  disabled={busy || !lineupDraft.attaquant || !lineupDraft.def_E || !lineupDraft.def_N || !lineupDraft.def_O}
                  onClick={() => run(async () => {
                    await setLineup(token, m.id, {
                      attaquant: lineupDraft.attaquant, def_E: lineupDraft.def_E,
                      def_N: lineupDraft.def_N, def_O: lineupDraft.def_O,
                      capitaine_defi: lineupDraft.capitaine_defi || undefined,
                    });
                    setLineupMatchId(null);
                  })}
                >
                  <SaveIcon size={14} /> Enregistrer la feuille de match
                </button>
              </div>
            </div>
          )}
        </div>
        {team.is_staff && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {m.etat === 'proposition' && isOpponent && (
              <>
                <button className="cia-btn cia-btn--small cia-btn--primary" disabled={busy} onClick={() => run(() => matchAction(token, m.id, 'accept'))}>Accepter</button>
                <button className="cia-btn cia-btn--small" disabled={busy} onClick={() => run(() => matchAction(token, m.id, 'reject'))}>Refuser</button>
              </>
            )}
            {m.etat === 'proposition' && isProposer && (
              <button className="cia-btn cia-btn--small cia-btn--danger" disabled={busy} onClick={() => run(() => matchAction(token, m.id, 'cancel'))}>Annuler</button>
            )}
            {(m.etat === 'ok' || m.etat === 'encours') && lineupMatchId !== m.id && (
              <button className="cia-btn cia-btn--small cia-btn--with-icon" disabled={busy} onClick={() => openLineupEditor(m)}>
                <ListGridIcon size={13} /> Feuille de match
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tm-detail">
        {error && <div className="compet-error">{error}</div>}

        <div className="tm-toolbar">
          {team.is_staff && (
            <>
              <button
                className={`cia-btn cia-btn--with-icon ${showChallengeForm ? 'cia-btn--primary' : ''}`}
                onClick={() => { setShowChallengeForm((v) => !v); setShowTrainingForm(false); }}
              >
                <CrossedSwordsTableIcon size={15} /> Proposer un défi
              </button>
              <button
                className={`cia-btn cia-btn--with-icon ${showTrainingForm ? 'cia-btn--primary' : ''}`}
                onClick={() => { setShowTrainingForm((v) => !v); setShowChallengeForm(false); }}
              >
                <TrainingIcon size={15} /> Caler un entraînement
              </button>
            </>
          )}
        </div>

        {showChallengeForm && (
          <div className="tm-form-card">
            <div className="tm-form-row">
              <div className="tm-field">
                <span>Équipe adverse</span>
                <TeamPicker value={opponent} onChange={setOpponent} placeholder="Équipe adverse…" excludeTeamId={team.id} />
              </div>
              <div className="tm-field">
                <span>Date &amp; heure</span>
                <QuarterHourDateTimeInput value={rdv} onChange={setRdv} min={minRdv} />
                {rdvTooSoon && (
                  <span style={{ color: 'var(--danger, #d9534f)', fontSize: '0.75rem' }}>
                    Le rendez-vous doit être au moins 5 minutes dans le futur
                  </span>
                )}
              </div>
              <div className="tm-field" style={{ width: 90 }}>
                <span>Parties</span>
                <input type="number" min={1} max={50} value={nbparties} onChange={(e) => setNbparties(Number(e.target.value))} />
              </div>
            </div>
            <div className="tm-form-checks">
              <label><input type="checkbox" checked={surcontrats} onChange={(e) => setSurcontrats(e.target.checked)} /> Surcontrats</label>
              <label><input type="checkbox" checked={attTournant} onChange={(e) => setAttTournant(e.target.checked)} /> Attaquant tournant</label>
            </div>
            <div className="tm-toolbar">
              <button className="cia-btn cia-btn--ghost" onClick={() => setShowChallengeForm(false)}>Annuler</button>
              <button
                className="cia-btn cia-btn--primary cia-btn--with-icon"
                disabled={busy || !opponent || rdvTooSoon}
                onClick={() => run(async () => {
                  await createMatch(token, {
                    opponent_team_id: opponent!.id,
                    rdv: new Date(rdv).toISOString(),
                    nbparties, surcontrats, att_tournant: attTournant,
                  });
                  setShowChallengeForm(false);
                  setOpponent(null);
                })}
              >
                <CheckCircleIcon size={15} /> Envoyer le défi
              </button>
            </div>
          </div>
        )}

        {showTrainingForm && (
          <div className="tm-form-card">
            <div className="tm-form-row">
              <div className="tm-field">
                <span>Date &amp; heure</span>
                <QuarterHourDateTimeInput value={trainingRdv} onChange={setTrainingRdv} min={minRdv} />
                {trainingRdvTooSoon && (
                  <span style={{ color: 'var(--danger, #d9534f)', fontSize: '0.75rem' }}>
                    Le rendez-vous doit être au moins 5 minutes dans le futur
                  </span>
                )}
              </div>
              <div className="tm-field" style={{ width: 90 }}>
                <span>Parties</span>
                <input type="number" min={1} max={50} value={trainingNbparties} onChange={(e) => setTrainingNbparties(Number(e.target.value))} />
              </div>
            </div>
            <div className="tm-toolbar">
              <button className="cia-btn cia-btn--ghost" onClick={() => setShowTrainingForm(false)}>Annuler</button>
              <button
                className="cia-btn cia-btn--primary cia-btn--with-icon"
                disabled={busy || trainingRdvTooSoon}
                onClick={() => run(async () => {
                  await createMatch(token, { opponent_team_id: 2, rdv: new Date(trainingRdv).toISOString(), nbparties: trainingNbparties });
                  setShowTrainingForm(false);
                })}
              >
                <CheckCircleIcon size={15} /> Créer l'entraînement
              </button>
            </div>
          </div>
        )}

        {loading && <div className="compet-loading">Chargement…</div>}

        {!loading && (
          <>
            <section>
              <h3 className="tm-section-title-sticky">À venir</h3>
              {upcoming.length === 0 && <div className="compet-empty">Aucun défi ni entraînement à venir</div>}
              {upcoming.map((m) => renderActiveMatch(m))}
            </section>

            {staleActive.length > 0 && (
              <section>
                <h3 className="tm-section-title-sticky">Propositions en attente (date dépassée)</h3>
                {staleActive.map((m) => renderActiveMatch(m, { stale: true }))}
              </section>
            )}

            <section>
              <h3 className="tm-section-title-sticky">Historique récent</h3>
              {past.length === 0 && <div className="compet-empty">Aucun défi passé</div>}
              {past.slice(0, 10).map((m) => {
                const isProposer = m.equipe1 === team.id;
                const isTraining = m.equipe2 === 2;
                const opponentName = isTraining ? 'Entraînement' : (isProposer ? m.equipe2_nom : m.equipe1_nom) || `Équipe #${isProposer ? m.equipe2 : m.equipe1}`;
                const myScore = isProposer ? m.pm1 : m.pm2;
                const theirScore = isProposer ? m.pm2 : m.pm1;
                const outcome = myScore == null || theirScore == null ? null : myScore > theirScore ? 'win' : myScore === theirScore ? 'draw' : 'loss';
                const outcomeColor = outcome === 'win' ? WIN_COLOR : outcome === 'loss' ? LOSS_COLOR : 'var(--text-muted)';
                const outcomeLabel = outcome === 'win' ? 'Victoire' : outcome === 'loss' ? 'Défaite' : outcome === 'draw' ? 'Nul' : null;
                return (
                  <div key={m.id} className="competition-card" style={{ cursor: 'default', marginBottom: 6 }}>
                    {isTraining ? (
                      <span className="tm-training-icon"><TrainingIcon size={20} /></span>
                    ) : (
                      <PlayerAvatar
                        pseudo={opponentName}
                        size={32}
                        photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(opponentName)}?size=64`}
                      />
                    )}
                    <div className="competition-card-text" style={{ flex: 1 }}>
                      <span className="competition-card-title">{isTraining ? opponentName : `vs ${opponentName}`}</span>
                      <span className="competition-card-subtitle">
                        {fmtDateTime(m.rdv || m.quand)} · {matchKindLabel(m)} · <StateBadge etat={m.etat} />
                      </span>
                    </div>
                    {myScore != null && theirScore != null && (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                        <span className="tc-team-block-name" style={{ fontWeight: 700, color: outcomeColor }}>
                          {fmtScore(myScore)} - {fmtScore(theirScore)}
                        </span>
                        {outcomeLabel && (
                          <span style={{ fontSize: 11, fontWeight: 600, color: outcomeColor }}>{outcomeLabel}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          </>
        )}
    </div>
  );
}
